"use client";
import React, { ReactNode, useMemo } from "react";

import Image from "next/image";
import Link from "next/link";

import {
  Box,
  Button,
  Container,
  Divider,
  Drawer,
  SxProps,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import { UniqueCruise } from "@/interfaces/IUniqueCruise";

import CloseIcon from "@mui/icons-material/CloseRounded";
import LocationIcon from "@mui/icons-material/LocationOnRounded";
import ShipIcon from "@mui/icons-material/DirectionsBoatFilledRounded";
import NightStayIcon from "@mui/icons-material/NightsStay";
import OpenTabIcon from "@mui/icons-material/OpenInNewRounded";

import EmbarkingDateIcon from "@mui/icons-material/TodayRounded";
// import ArrowDownIcon from "@mui/icons-material/ArrowDownwardRounded";
import { UniqueShip } from "@/interfaces/IUniqueShip";
import SocialIcons from "@/components/home-redesigned/SocialIcons";
import { chunkArray } from "@/helpers/helpers";

export const ItineraryHero: React.FC<{
  image: string;
  cruiseName?: string;
  cruiseType?: string[];
  regions?: string[];
  cruiseData?: UniqueCruise;
  operatorImg?: string;
  shipData?: UniqueShip;
  isUniqueShip?: boolean;
}> = ({
  image,
  cruiseName,
  cruiseType,
  regions,
  cruiseData,
  operatorImg,
  shipData,
  // isUniqueShip,
}) => {
  const arrayMapedShipFacts = useMemo(
    () => [
      { label: "Guest Capacity", value: shipData?.shipFacts?.capacity?.toString(), unit: null },
      { label: "No. of Cabins", value: shipData?.shipFacts?.cabinCount?.toString(), unit: null },
      { label: "Crew Members", value: shipData?.shipFacts?.crewCount?.toString(), unit: null },
      { label: "No. of Decks", value: shipData?.shipFacts?.deckCount?.toString(), unit: null },
      {
        label: "Wheelchair Cabins",
        value: shipData?.shipFacts?.wheelchairCabinCount?.toString(),
        unit: null,
      },
      { label: "Launch Year", value: shipData?.shipFacts?.launchYear?.toString(), unit: null },
      {
        label: "Large Cabins",
        value: shipData?.shipFacts?.largeCabinCount?.toString(),
        unit: null,
      },
      { label: "Refit Year", value: shipData?.shipFacts?.refitYear?.toString(), unit: null },
      { label: "Length", value: shipData?.shipFacts?.length?.toString(), unit: "m" },
      { label: "Width", value: shipData?.shipFacts?.width?.toString(), unit: "m" },
      { label: "Speed", value: shipData?.shipFacts?.speed?.toString(), unit: "Knots" },
      { label: "Gross Tonnage", value: shipData?.shipFacts?.grossTonnage?.toString(), unit: null },
      { label: "Language", value: shipData?.shipFacts?.language?.toString(), unit: null },
      { label: "Currency", value: shipData?.shipFacts?.currency?.toString(), unit: null },
      {
        label: "Electrical Plugs",
        value: shipData?.shipFacts?.electricalPlugs?.types?.join(", "),
        unit: null,
      },
    ],
    [shipData?.shipFacts],
  );

  const filteredFacts = useMemo(
    () => arrayMapedShipFacts.filter((x) => x.value && x.value !== "0"),
    [arrayMapedShipFacts],
  );

  return (
    <Box>
      {/* NEW UI - STARTS */}
      <Box sx={{ width: "100%", pt: "14px", position: "relative", bgcolor: "#edf1ff" }}>
        <Container sx={{ p: { xs: "1rem 1rem 0rem 1rem", sm: "1.5rem 1.5rem 0rem 1.5rem" } }}>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "auto 220px" },
              gap: "10px",
              alignItems: "center",
            }}
          >
            <Box sx={{ width: "100%", height: "auto" }}>
              {/* 01. Nights & Cruise Type */}
              {cruiseData && (
                <Box sx={{ display: "flex", gap: "10px", alignItems: "center", mb: "10px" }}>
                  {cruiseData?.cruiseNights && (
                    <Box
                      sx={{
                        bgcolor: "#FFBB00",
                        color: "#350097",
                        border: "2px solid #FFBB00",
                        fontSize: { xs: "1rem", md: "1.2rem" },
                        fontWeight: 700,
                        fontFamily: "'blauerNue', 'blauerNue Fallback'",
                        borderRadius: "6px",
                        padding: { xs: "3px 7px 2px 9px", md: "4px 8px 3px 9px" },
                        width: "fit-content",
                        display: "flex",
                        alignItems: "center",
                        gap: { xs: "3px", md: "5px" },
                        svg: {
                          ml: "-4px",
                          width: { xs: "20px", md: "22px" },
                        },
                      }}
                    >
                      <NightStayIcon />
                      <Box>{cruiseData?.cruiseNights} Nights</Box>
                    </Box>
                  )}

                  {cruiseType && (
                    <Box
                      sx={{
                        color: "#fff",
                        bgcolor: "#E30053",
                        border: "2px solid #FF7DAC",
                        borderRadius: "15px",
                        fontSize: { xs: "1rem", md: "1.2rem" },
                        fontWeight: 600,
                        fontFamily: "'blauerNue', 'blauerNue Fallback'",
                        width: "fit-content",
                        padding: { xs: "5px 10px 5px", md: "3px 10px 4px" },
                      }}
                    >
                      {cruiseType} Cruise
                    </Box>
                  )}
                </Box>
              )}

              {/* 02. Cruise Name */}
              <Typography
                variant="h1"
                sx={{
                  fontSize: { xs: "1.6rem", sm: "2rem", md: "3rem" },
                  fontWeight: 700,
                  color: "primary.main",
                }}
              >
                {cruiseName}
                {regions && (
                  <Box
                    sx={{
                      display: { xs: "flex", sm: "inline" },
                      fontSize: { xs: "18px", md: "26px" },
                      lineHeight: 1.1,
                      fontWeight: 400,
                      bgcolor: { xs: "#c9d5ff", sm: "#060273" },
                      color: { xs: "#060273", sm: "#fff" },
                      p: { xs: "7px 15px 3px 6px", sm: "3px 19px 4px 11px" },
                      width: "fit-content",
                      borderRadius: "18px",
                      mt: { xs: "8px", md: "0px" },
                      ml: { xs: "0px", md: "12px" },
                      top: { xs: "0px", md: "-9px" },
                      position: "relative",
                      textWrap: "nowrap",
                      svg: {
                        width: { xs: "20px", md: "25px" },
                        height: { xs: "20px", md: "25px" },
                        position: "relative",
                        top: { xs: "-2px", sm: "4px" },
                        ml: { xs: "0px", sm: "-4px" },
                        mr: { xs: "-3px", sm: "0px" },
                      },
                    }}
                  >
                    <LocationIcon /> {regions}
                  </Box>
                )}
              </Typography>

              {/* 03. Price From */}
              {cruiseData?.cruiseOnlyPrice && (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", sm: "row" },
                    alignItems: { xs: "flex-start", sm: "center" },
                    justifyContent: { xs: "space-between", sm: "flex-start" },
                    gap: "1rem",
                    mt: { xs: "20px", md: "10px" },
                    width: "100%",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      gap: "1rem",
                      width: { xs: "100%", sm: "fit-content" },
                    }}
                  >
                    <Box sx={{ position: "relative" }}>
                      <Box
                        sx={{
                          fontSize: "14px",
                          fontWeight: 700,
                          fontFamily: "'blauerNue', 'blauerNue Fallback'",
                          mt: { xs: "0px", sm: "8px" },
                          lineHeight: 1.2,
                        }}
                      >
                        PRICE FROM
                      </Box>
                      <Typography
                        variant="h5"
                        sx={{
                          mt: "-0px",
                          color: "#E30053",
                          fontSize: "30px",
                          lineHeight: 1.1,
                          span: {
                            color: "#A8215C",
                            fontSize: "24px",
                          },
                          ".gbp": {
                            fontWeight: 900,
                            mr: "2px",
                          },
                          ".pp": {
                            fontWeight: 400,
                            ml: "2px",
                          },
                        }}
                      >
                        <span className="gbp">£</span>
                        {Number(cruiseData?.cruiseOnlyPrice)
                          ? cruiseData?.cruiseOnlyPrice
                          : cruiseData?.flyCruisePrice}
                        <span className="pp">pp</span>

                        {!Number(cruiseData?.cruiseOnlyPrice) &&
                          Number(cruiseData?.flyCruisePrice) !== 0 && (
                            <Box
                              sx={{
                                fontSize: "12px",
                                fontWeight: 400,
                                width: "max-content",
                                color: "#060273",
                              }}
                            >
                              (Cruise with Flight)
                            </Box>
                          )}
                      </Typography>

                      {/* <Box
                        sx={{
                          fontSize: "12px",
                          color: "#666",
                          fontFamily: "'blauerNue', 'blauerNue Fallback'",
                          display: { xs: "none", sm: "block" },
                        }}
                      >
                        View all Pricing options
                      </Box> */}
                    </Box>

                    {/* <Button
                      sx={{
                        color: "#fff",
                        fontFamily: "'blauerNue', 'blauerNue Fallback'",
                        p: "6px 10px 5px 12px",
                        textTransform: "initial",
                        display: { xs: "flex", sm: "none" },
                        gap: "10px",
                        textAlign: "left",
                        lineHeight: 1.3,
                        fontSize: "14px",
                      }}
                    >
                      <Box>
                        Explore all <br /> Fare Options
                      </Box>
                      <ArrowDownIcon
                        sx={{
                          width: "35px",
                          height: "35px",
                          flexShrink: 1,
                        }}
                      />
                    </Button> */}
                  </Box>

                  <EmbarkingReturningDatesUI
                    startsOn={cruiseData?.startsOn}
                    startsAt={cruiseData?.startsAt}
                    endsOn={cruiseData?.endsOn}
                    endsAt={cruiseData?.endsAt}
                    sx={{
                      width: { xs: "calc(100% + 32px)", sm: "fit-content" },
                      ml: { xs: "-16px", sm: "0px" },
                      mt: { xs: "2px", sm: "0px" },
                      mb: { xs: "-2px", sm: "0px" },
                    }}
                  />
                </Box>
              )}
            </Box>

            {/* Operator Image */}
            {operatorImg && (
              <Box
                sx={{
                  position: "relative",
                  display: { xs: "none", md: "grid" },
                  placeItems: "center",
                  height: {
                    xs: "calc(100% - 70px)",
                    md: "calc(100% - 80px)",
                    lg: "fit-content",
                  },
                  maxHeight: "300px",
                  minHeight: "120px",
                  bgcolor: "#fff",
                  borderRadius: "20px",
                  border: "3px solid #06027350",
                  width: "100%",
                  p: "1rem",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    height: "100px",
                    width: "100%",
                    maxHeight: "300px",
                  }}
                >
                  <Image
                    src={operatorImg}
                    alt={shipData?.operator?.name ?? "Cruise operator"}
                    fill
                    unoptimized
                    loader={({ src }) => src}
                    style={{
                      objectFit: "contain",
                    }}
                  />
                </Box>
                <Typography
                  variant="body2"
                  sx={{
                    position: "relative",
                    textAlign: "center",
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#06027350",
                    width: "100%",
                    fontFamily: "'blauerNue', 'blauerNue Fallback'",
                  }}
                >
                  Your Exclusive Cruise Partner
                </Typography>
              </Box>
            )}
          </Box>

          <Grid container spacing={2} sx={{ mt: { xs: "0px", sm: "1rem", md: "2.5rem" } }}>
            <Grid size={{ xs: 12, md: 7.5 }}>
              <Box
                sx={{
                  mb: { x: "1.5rem", sm: "0px" },
                  overflow: "hidden",
                  width: { xs: "calc(100% + 32px)", sm: "100%" },
                  minHeight: { xs: "250px", md: "280px" },
                  height: { xs: "250px", md: "100%" },
                  position: "relative",
                  left: { xs: "-16px", sm: "unset" },
                  borderRadius: { xs: "0px", sm: "20px" },
                  border: { xs: "none", sm: "2px solid #fff" },
                  boxShadow: { xs: "unset", sm: "0px 0px 12px #77777770" },
                  img: {
                    objectFit: "cover",
                    width: "100%",
                    height: "100%",
                    aspectRatio: { xs: "9/6", md: "8/2" },
                  },
                }}
              >
                {/* Ship Cover Image */}
                <Image
                  src={image}
                  alt={"celebrity apex"}
                  width={900}
                  height={400}
                  unoptimized
                  loader={({ src }) => src}
                  priority
                  // className="panning_image"
                />

                {/* Ship Name */}
                <HeroShipUI title={shipData?.title} operatorName={shipData?.operator?.name} />

                {operatorImg && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: { xs: "14px", sm: "10px" },
                      right: { xs: "16px", sm: "10px" },
                      display: { xs: "grid", md: "none" },
                      placeItems: "center",
                      bgcolor: "#fff",
                      borderRadius: "12px",
                      width: "100px",
                      p: "10px",
                      img: {
                        width: "100%",
                        height: "auto",
                        objectFit: "contain",
                      },
                    }}
                  >
                    <Image src={operatorImg} alt={shipData?.operator?.name ?? "Cruise operator"} width={100} height={50} />
                  </Box>
                )}
              </Box>
            </Grid>

            <Grid size={{ xs: 12, md: 4.5 }}>
              <Box
                sx={{
                  background:
                    "linear-gradient(90deg,rgba(6, 2, 115, 1) 0%, rgba(67, 0, 199, 1) 100%)",
                  overflow: "hidden",
                  width: { xs: "calc(100% + 32px)", sm: "100%" },
                  height: { xs: "auto", md: "auto" },
                  position: "relative",
                  left: { xs: "-16px", sm: "unset" },
                  borderRadius: { xs: "0px", sm: "20px" },
                  border: { xs: "none", sm: "3px solid #060273" },
                  boxShadow: "0px 0px 12px #A879FF70",
                  mt: { xs: "-17px", sm: "0px" },
                }}
              >
                <Box
                  sx={{
                    p: "8px 14px",
                    color: "#fff",
                    fontWeight: 300,
                    fontSize: "15px",
                    textAlign: "center",
                  }}
                >
                  Ship Specifications at a Glance
                </Box>

                <Box
                  sx={{
                    p: "14px 14px 14px",
                    background:
                      "linear-gradient(90deg,rgba(223, 229, 253, 1) 0%, rgba(242, 244, 253, 1) 100%)",
                    borderRadius: { xs: "0px", sm: "17px" },
                    height: "calc(100%)",
                    width: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box sx={{ width: "100%" }}>
                    {chunkArray(filteredFacts, 3).map((factRow, fRI, self) => (
                      <Box
                        key={fRI}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "1fr 1fr 1fr",
                          gap: "10px 22px",
                          mb: self.length - 1 == fRI ? "0px" : "14px",
                          position: "relative",
                          "&:after": {
                            content: '""',
                            width: "100%",
                            height:
                              self.length - 1 == fRI && filteredFacts.length > 12 ? "0px" : "1px",
                            bgcolor: "#C7D4FF",
                            position: "absolute",
                            top: {
                              xs: self.length - 1 == fRI ? "43px" : "calc(100% + 5px)",
                              md: self.length - 1 == fRI ? "calc(50% - 5px)" : "calc(100% + 5px)",
                            },
                            left: "0px",
                          },
                        }}
                      >
                        {factRow.map(
                          (fact, fI) =>
                            fact.value && (
                              <ShipFactUI
                                key={fI}
                                label={fact.label}
                                value={fact.value}
                                unit={fact.unit}
                                sx={{
                                  ...(fact.label == "Electrical Plugs" && {
                                    gridColumn:
                                      filteredFacts.length % 3 == 2
                                        ? "2/4"
                                        : (shipData?.shipFacts?.electricalPlugs?.types?.length ??
                                            0) > 2
                                        ? "1/4"
                                        : "1/3",
                                  }),
                                }}
                              />
                            ),
                        )}
                      </Box>
                    ))}
                  </Box>
                </Box>
              </Box>
            </Grid>
          </Grid>
        </Container>

        <Container>
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", xl: "row" },
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: { xs: "1rem", md: "1.5rem", xl: "1rem" },
              mt: { xs: "0rem", md: "2rem", xl: "0rem" },
              mb: "0rem",
            }}
          >
            <Box sx={{}}>
              <Box
                sx={{
                  color: "#fff",
                  fontSize: "1rem",
                  fontWeight: 600,
                  mb: "10px",
                  mt: { xs: "10px", xl: "0px" },
                  fontFamily: "'blauerNue', 'blauerNue Fallback'",
                  textAlign: { xs: "center", xl: "left" },
                }}
              >
                Follow Us for Exclusive Updates!
              </Box>

              <Box>
                <SocialIcons sx={{ display: "flex", justifyContent: "center" }} />
              </Box>
            </Box>

            <Box sx={{ display: { xs: "none", md: "block" } }}>
              <Box
                sx={{
                  fontFamily: "'blauerNue', 'blauerNue Fallback'",
                  fontSize: "14px",
                  color: "#fff",
                  fontWeight: 400,
                  textAlign: { xs: "center", xl: "right" },
                  a: {
                    color: "#ff1184",
                    svg: {
                      mb: "-6px",
                      width: "21px",
                      height: "21px",
                      ml: "4px",
                    },
                  },
                }}
              >
                We offer flexible ways to pay for your dream cruise –{" "}
                <Link href="/payment">
                  Explore Payment Options!
                  <OpenTabIcon />
                </Link>
              </Box>

              <Box
                sx={{
                  position: "relative",
                  display: "flex",
                  justifyContent: "center",
                  textAlign: "center",
                  mt: "10px",
                  mb: "16px",
                  width: { xs: "100%", xl: "800px" },
                  img: {
                    objectFit: "contain",
                    width: "100%",
                    maxWidth: { xs: "100%", md: "900px", xl: "1200px" },
                    height: "auto",
                  },
                }}
              >
                <Image
                  src="/images/home/new-design/payment-icons-densed.png"
                  alt="Payment Icons"
                  width={800}
                  height={60}
                  loader={({ src }) => src}
                />
              </Box>
            </Box>
          </Box>
        </Container>

        <Box
          sx={{
            width: "100%",
            height: { xs: "150px", sm: "120px", md: "215px" },
            bgcolor: "#000c37",
            mt: { xs: "-145px", sm: "-90px", md: "-200px" },
            display: "block",
          }}
        ></Box>
      </Box>
    </Box>
  );
};

export const EmbarkingReturningDatesUI: React.FC<{
  startsOn?: string | null | undefined;
  startsAt?: string | null | undefined;
  endsOn?: string | null | undefined;
  endsAt?: string | null | undefined;
  sx?: SxProps<Theme>;
}> = ({ startsOn, startsAt, endsOn, endsAt, sx }) => {
  const isMobile = useMediaQuery("(max-width: 600px)");

  return (
    <Box
      sx={{
        mt: "8px",
        display: "flex",
        gap: "10px",
        alignItems: "center",
        bgcolor: { xs: "#5600FF", sm: "#c9d5ff" },
        color: { xs: "#fff", sm: "#000" },
        border: { xs: "unset", sm: "1px solid #b3afff" },
        p: { xs: "9px 10px 8px 12px", md: "8px 10px 7px 8px" },
        borderRadius: { xs: "0px", sm: "10px" },
        svg: {
          width: "35px",
          height: "35px",
        },
        ...sx,
      }}
    >
      <EmbarkingDateIcon />

      <Box>
        <Box sx={{ fontSize: { xs: "12px", md: "13px" } }}>Embarking on</Box>
        <Box
          sx={{
            fontSize: { xs: "15px", md: "18px" },
            fontFamily: "'blauerNue', 'blauerNue Fallback'",
            fontWeight: 700,
          }}
        >
          {isMobile
            ? `${new Date(startsOn ?? "").toLocaleDateString("en-GB", {
                day: "numeric",
                month: "short",
                year: "numeric",
              })}`
            : `${new Date(startsOn ?? "").toLocaleDateString("en-GB", {
                day: "numeric",
                month: "long",
                year: "numeric",
              })}`}
        </Box>

        <Box
          sx={{
            fontSize: "13px",
            fontFamily: "'blauerNue', 'blauerNue Fallback'",
          }}
        >
          at <span>{startsAt}</span>
        </Box>
      </Box>

      <Divider orientation="vertical" variant="middle" flexItem sx={{ my: "0px" }} />

      <Box>
        <Box>
          <Box sx={{ fontSize: { xs: "12px", md: "13px" } }}>Returning on</Box>
          <Box
            sx={{
              fontSize: { xs: "15px", md: "18px" },
              fontFamily: "'blauerNue', 'blauerNue Fallback'",
              fontWeight: 700,
            }}
          >
            {isMobile
              ? `${new Date(endsOn ?? "").toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "short",
                  year: "numeric",
                })}`
              : `${new Date(endsOn ?? "").toLocaleDateString("en-GB", {
                  day: "numeric",
                  month: "long",
                  year: "numeric",
                })}`}
          </Box>
          <Box
            sx={{
              fontSize: "13px",
              fontFamily: "'blauerNue', 'blauerNue Fallback'",
            }}
          >
            at <span>{endsAt}</span>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const ReadMoreDrawer: React.FC<{
  isReadMore: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
}> = ({ isReadMore, onClose, title, children }) => {
  return (
    <Drawer
      anchor="bottom"
      open={isReadMore}
      onClose={onClose}
      sx={{
        zIndex: 1204,
        ".MuiPaper-root.MuiDrawer-paper": {
          borderRadius: "15px 15px 0 0",
          overflowY: "visible",
          maxHeight: "calc(100% - 86px)",
          minHeight: "50%",
        },
      }}
    >
      <Button
        onClick={onClose}
        aria-label="Close"
        sx={{
          position: "absolute",
          top: "-3rem",
          right: "16px",
          minWidth: "unset",
          p: "4px",
          bgcolor: "#fff",
          color: "primary.main",
          "&:hover": {
            bgcolor: "primary.main",
            color: "#fff",
          },
        }}
      >
        <CloseIcon />
      </Button>
      <Typography
        sx={{
          fontSize: "1.2rem",
          fontWeight: 500,
          p: "9px 16px 4px 16px",
          fontFamily: "'blauerNue', 'blauerNue Fallback'",
          borderBottom: "1px solid #ccc",
          color: "#060273",
          position: "sticky",
          top: "0px",
        }}
      >
        {title}
      </Typography>
      <Box sx={{ p: "1rem", overflowY: "scroll" }}>{children}</Box>
    </Drawer>
  );
};

const HeroShipUI: React.FC<{
  title?: string | null; //shipData?.title
  operatorName?: string | null; //shipData?.operator?.name
}> = ({ title, operatorName }) => {
  return (
    <>
      <Box
        sx={{
          position: "absolute",
          top: { xs: "unset", md: "13px" },
          bottom: { xs: "33px", md: "unset" },
          right: { xs: "unset", md: "13px" },
          left: { xs: "15px", md: "unset" },
          zIndex: 10,
        }}
      >
        <Box
          sx={{
            position: "relative",
            display: "flex",
            gap: { xs: "8px", sm: "10px" },
            alignItems: "center",
            bgcolor: "#fff",
            p: { xs: "7px 8px 7px 7px", sm: "10px 13px 10px 10px" },
            borderRadius: "10px",
            svg: {
              width: { xs: "24px", sm: "30px" },
              height: { xs: "24px", sm: "30px" },
            },
          }}
        >
          {title && (
            <>
              <ShipIcon />
              <Box>
                <Box sx={{ fontSize: "13px" }}>Sailing in Style with</Box>
                <Box
                  sx={{
                    fontSize: { xs: "16px", sm: "18px" },
                    fontFamily: "'blauerNue', 'blauerNue Fallback'",
                    fontWeight: 600,
                  }}
                >
                  {title}
                </Box>
              </Box>
            </>
          )}

          {operatorName && (
            <Box
              sx={{
                position: "absolute",
                bottom: "-22px",
                left: { xs: "0px", md: "12px" },
                color: "#fff",
                fontSize: { xs: "14px", sm: "15px" },
                fontFamily: "'blauerNue', 'blauerNue Fallback'",
                textShadow: "0px 0px 4px rgba(0,0,0,0.4)",
                width: "300px",
                span: {
                  fontWeight: 700,
                },
              }}
            >
              by <span>{operatorName}</span>
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

const ShipFactUI: React.FC<{
  label: string;
  value: string;
  unit?: string | null;
  sx?: SxProps<Theme>;
}> = ({ label, value, unit, sx }) => {
  return (
    <Box sx={{ ...sx }}>
      <Box
        sx={{
          fontSize: { xs: "14px", sm: "16px" },
          fontFamily: "'blauerNue', 'blauerNue Fallback'",
          fontWeight: 300,
          color: "#5450BE",
        }}
      >
        {label}
      </Box>
      <Box
        sx={{
          fontFamily: "'blauerNue', 'blauerNue Fallback'",
          fontWeight: 700,
          display: "flex",
          alignItems: "center",
          color: "#060273",
        }}
      >
        <Box
          sx={{
            fontSize: { xs: "18px", sm: "22px" },
          }}
        >
          {value}
        </Box>
        <Box
          sx={{
            position: "relative",
            fontSize: { xs: "14px", md: "14px" },
            top: "-4px",
            left: "3px",
          }}
        >
          {unit}
        </Box>
      </Box>
    </Box>
  );
};
